import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { styled } from '@compiled/react';
import formatISO from 'date-fns/formatISO';
import type { DocNode } from '@atlaskit/adf-schema';
import Button from '@atlaskit/button';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import { Field, ErrorMessage } from '@atlaskit/form';
import ChevronRightLargeIcon from '@atlaskit/icon/utility/migration/chevron-right--chevron-right-large';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down--hipchat-chevron-down';
import { token } from '@atlaskit/tokens';
import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import availableTimes from '@atlassian/jira-common-constants/src/datetimepicker-available-times.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	LOG_WORK_DATE_ERROR_KEY,
	LOG_WORK_DESCRIPTION_ERROR_KEY,
	LOG_WORK_TIME_REMAINING_ERROR_KEY,
	LOG_WORK_TIME_SPENT_ERROR_KEY,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import { AsyncRichTextEditView } from '@atlassian/jira-issue-field-rich-text/src/async.tsx';
import { TimeEstimate } from '@atlassian/jira-issue-time-estimate/src/ui/edit/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { getLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { FieldContainer } from '../../common/ui/fields/styled.tsx';
import { withFormField } from '../../common/ui/with-form-field/index.tsx';
import {
	DATE_STARTED_FIELD_NAME,
	DESCRIPTION_FIELD_NAME,
	TIME_REMAINING_FIELD_NAME,
	TIME_SPENT_FIELD_NAME,
} from './constants.tsx';
import messages from './messages.tsx';
import type { Props, LogWorkFieldConfig, FieldValue, LogWorkValue } from './types.tsx';
import { getIsTimeEstimateExceeded, getCalculatedTimeRemainingIfAny } from './utils.tsx';

const skeletonHeight = `${gridSize * 18.75}px`;

const LogWorkField = (props: Props) => {
	const currentDateTime = useMemo(() => formatISO(new Date()), []);
	const { formatMessage } = useIntl();

	const [timeFieldInFocus, setTimeFieldInFocus] = useState('');
	const isDefaultValueSetInFormStateForDateField = useRef(false);
	const [areLogWorkFieldsVisible, showLogWorkFields] = useState(false);
	const [estimateExceededWarning, setEstimateExceededWarning] = useState<string>('');
	const {
		width = 350,
		fieldProps: {
			value = {
				timeRemaining: '',
				timeSpent: '',
				dateValue: '',
				description: undefined,
			},
			onChange,
		},
		timeTrackingValue = '',
		mediaContext,
		autoFocus = false,
		additionalData,
		isRequired,
		errors,
		fieldName,
	} = props;

	const { description, timeRemaining, timeSpent, dateValue } = value;

	const { timeTrackingSettings: config } = additionalData;
	const locale = useLocale();

	const onChangeFieldValue = useCallback(
		(partialFieldValue: Partial<LogWorkValue>) => {
			onChange({
				...value,
				...partialFieldValue,
			});
		},
		[onChange, value],
	);

	useEffect(() => {
		if (timeTrackingValue) {
			const isTimeEstimateExceeded = getIsTimeEstimateExceeded({
				config,
				timeRemaining,
				timeSpent,
				timeTrackingValue,
			});
			setEstimateExceededWarning(
				isTimeEstimateExceeded ? formatMessage(messages.originalEstimateExceededNonFinal) : '',
			);
		} else if (estimateExceededWarning) {
			setEstimateExceededWarning('');
		}
	}, [timeRemaining, timeSpent, timeTrackingValue, config, formatMessage, estimateExceededWarning]);

	useEffect(() => {
		if (
			!isDefaultValueSetInFormStateForDateField.current &&
			!dateValue &&
			areLogWorkFieldsVisible
		) {
			isDefaultValueSetInFormStateForDateField.current = true;
			onChangeFieldValue({
				dateValue: currentDateTime,
			});
		}
	}, [dateValue, currentDateTime, onChangeFieldValue, areLogWorkFieldsVisible]);

	const onChangeTimeSpent = useCallback(
		(newTimeSpent: string) => {
			let newTimeRemaining = timeRemaining;
			if (timeTrackingValue) {
				const calculatedTimeRemainingIfAny = getCalculatedTimeRemainingIfAny({
					timeSpent: newTimeSpent,
					timeTrackingValue,
					config,
				});

				if (calculatedTimeRemainingIfAny !== null) {
					newTimeRemaining = calculatedTimeRemainingIfAny;
				}
			}
			setTimeFieldInFocus(TIME_SPENT_FIELD_NAME);
			onChangeFieldValue({
				timeSpent: newTimeSpent,
				timeRemaining: newTimeRemaining,
			});
		},
		[config, onChangeFieldValue, timeRemaining, timeTrackingValue],
	);

	const onChangeTimeRemaining = useCallback(
		(newTimeRemaining: string) => {
			setTimeFieldInFocus(TIME_REMAINING_FIELD_NAME);
			onChangeFieldValue({
				timeRemaining: newTimeRemaining,
			});
		},
		[onChangeFieldValue],
	);

	const onToggleLogWorkFieldsVisibility = useCallback(() => {
		showLogWorkFields((current) => !current);
	}, []);

	const onChangeDatePicker = useCallback(
		(newDateValue: string | undefined) => {
			onChangeFieldValue({
				dateValue: newDateValue,
			});
		},
		[onChangeFieldValue],
	);

	const onChangeEditorValue = useCallback(
		(newEditorValue: DocNode | undefined) => {
			onChangeFieldValue({
				description: newEditorValue,
			});
		},
		[onChangeFieldValue],
	);

	const isEstimateExceeded = estimateExceededWarning !== '';

	const weekStartDay = getLocale(locale)?.options?.weekStartsOn;
	const buttonLabel = formatMessage(messages.logWorkLabel);
	const dateTimeLabel = formatMessage(messages.dateStarted);

	return (
		<>
			<LogWorkHeader>
				<Button
					iconBefore={
						areLogWorkFieldsVisible ? (
							<ChevronDownIcon spacing="spacious" label="" />
						) : (
							<ChevronRightLargeIcon spacing="spacious" label="" />
						)
					}
					appearance="subtle"
					spacing="none"
					onClick={onToggleLogWorkFieldsVisibility}
					testId="issue-create-commons.ui.log-work-field.next-button"
					aria-label={buttonLabel}
					aria-expanded={areLogWorkFieldsVisible}
				/>
				<span>
					{buttonLabel} {isRequired === true ? <RequiredDot>*</RequiredDot> : ''}
				</span>
			</LogWorkHeader>
			{areLogWorkFieldsVisible && (
				<>
					<FieldContainer width={width}>
						<Field
							name={TIME_SPENT_FIELD_NAME}
							label={formatMessage(messages.timeSpent)}
							isRequired={isRequired}
						>
							{({ fieldProps }) => (
								<>
									<TimeEstimate
										{...fieldProps}
										autoFocus={autoFocus}
										value={timeSpent}
										onChange={onChangeTimeSpent}
										shouldShowWarning={
											isEstimateExceeded && timeFieldInFocus === TIME_SPENT_FIELD_NAME
										}
										config={config}
									/>
									{errors && errors[LOG_WORK_TIME_SPENT_ERROR_KEY] ? (
										<ErrorMessage>{errors[LOG_WORK_TIME_SPENT_ERROR_KEY]}</ErrorMessage>
									) : null}
								</>
							)}
						</Field>
					</FieldContainer>
					<FieldContainer width={width}>
						<Field name={TIME_REMAINING_FIELD_NAME} label={formatMessage(messages.timeRemaining)}>
							{({ fieldProps }) => (
								<>
									<TimeEstimate
										{...fieldProps}
										value={timeRemaining}
										onChange={onChangeTimeRemaining}
										shouldShowWarning={
											isEstimateExceeded && timeFieldInFocus === TIME_REMAINING_FIELD_NAME
										}
										config={config}
									/>
									{errors && errors[LOG_WORK_TIME_REMAINING_ERROR_KEY] ? (
										<ErrorMessage>{errors[LOG_WORK_TIME_REMAINING_ERROR_KEY]}</ErrorMessage>
									) : null}
								</>
							)}
						</Field>
					</FieldContainer>
					<FieldContainer width={width}>
						<Field name={DATE_STARTED_FIELD_NAME} label={dateTimeLabel} isRequired={isRequired}>
							{({ fieldProps }) => (
								<>
									<DateTimePicker
										onChange={onChangeDatePicker}
										value={dateValue}
										locale={locale}
										// @ts-expect-error - TS2322 - Type '{ onChange: (newDateValue: any) => void; value: string; locale: Locale; isClearable: boolean; times: string[]; }' is not assignable to type 'IntrinsicAttributes & Pick<Pick<Pick<DateTimePickerProps, "name" | "value" | "locale" | "id" | "autoFocus" | "defaultValue" | "onFocus" | ... 17 more ... | "parseValue">, "value" | ... 4 more ... | "parseValue"> & Partial<...> & Partial<...> & RefAttributes<...> & WithContextProps, "name" | ... 25 more ... | "parseV...'.
										isClearable={!isRequired}
										datePickerProps={{
											weekStartDay,
											label: dateTimeLabel,
											id: fieldProps.id,
											shouldShowCalendarButton: true,
										}}
										timePickerProps={{
											times: availableTimes,
										}}
									/>
									{errors && errors[LOG_WORK_DATE_ERROR_KEY] ? (
										<ErrorMessage>{errors[LOG_WORK_DATE_ERROR_KEY]}</ErrorMessage>
									) : null}
								</>
							)}
						</Field>
					</FieldContainer>
					<Field name={DESCRIPTION_FIELD_NAME} label={formatMessage(messages.description)}>
						{() => (
							<>
								<Placeholder
									name="rich-text-edit-view"
									fallback={<Skeleton height={skeletonHeight} />}
								>
									<AsyncRichTextEditView
										onChange={onChangeEditorValue}
										defaultValue={description}
										// @ts-expect-error - TS2322 - Type 'Readonly<{ uploadContext: UploadContext | null; userAuth: UserAuth | null; viewContext: ViewContext | null; }> | {} | undefined' is not assignable to type 'Readonly<{ uploadContext: UploadContext | null; userAuth: UserAuth | null; viewContext: ViewContext | null; }>'.
										mediaContext={mediaContext}
										// mentionProvider will be added once support is there on the backend
										// @ts-expect-error - TS2739 - Type '{ unsubscribe: () => void; }' is missing the following properties from type 'MentionProvider': filter, recordMentionSelection, shouldHighlightMention, isFiltering, subscribe
										mentionProvider={{
											// was throwing a console error on unmouting so added this
											// Replace with lodash/noop
											// eslint-disable-next-line @typescript-eslint/no-empty-function
											unsubscribe: () => {},
										}}
										assistiveLabel={fieldName}
									/>
								</Placeholder>
								{errors && errors[LOG_WORK_DESCRIPTION_ERROR_KEY] ? (
									<ErrorMessage>{errors[LOG_WORK_DESCRIPTION_ERROR_KEY]}</ErrorMessage>
								) : null}
							</>
						)}
					</Field>
					{errors &&
						errors.logWorkOtherErrors?.map((error: string, index: number) => (
							<ErrorMessage key={`logwork-${index}-common-error`}>{error}</ErrorMessage>
						))}
				</>
			)}
		</>
	);
};

export { LogWorkField };

export default withFormField({
	shouldHideLabel: true,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element' is not assignable to parameter of type 'AbstractComponent<ComponentProps<LogWorkFieldConfig, FieldValue>, any>'.
})<LogWorkFieldConfig, FieldValue>(LogWorkField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequiredDot = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.danger'),
	font: token('font.body.UNSAFE_small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogWorkHeader = styled.div({
	display: 'flex',
	alignItems: 'center',
});
